import type { FunctionReturnType } from 'convex/server';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
  getSowingConfigForPlant,
  type PlantSowingConfig,
  type UserPlantDefault,
  type UserPreferences,
} from '@modules/garden-config/business_logic';
import type { Garden } from '@modules/my-gardens/business_logic';
import {
  dateForSowWeekOfMonth,
  formatSowTimingLabel,
} from '@modules/sowing-calendar/business_logic';

export type { Garden };

export type Plant = FunctionReturnType<typeof api.plants.queries.getAllPlants>[number];

export type { PlantSowingConfig, UserPlantDefault, UserPreferences };

export interface SeedRequirement {
  plantId: string;
  plantName: string;
  emoji: string;
  imagePath?: string;
  category: string;
  seedsNeeded: number;
  hasSown?: boolean;
  /** Configured sow month/week label, e.g. "Aug · 1". Empty when unset. */
  sowTimingLabel: string;
  /**
   * Whole days until this year's sow week starts (positive), 0 when due now,
   * negative when the sow week has already passed; null when unset.
   */
  daysUntilSow: number | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfSowWeek(start: Date): Date {
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return end.getTime() > monthEnd.getTime() ? monthEnd : end;
}

/**
 * Days relative to this year's sow week: positive = upcoming, 0 = due now,
 * negative = days since week start after the window has passed.
 */
export function daysUntilSowTiming(
  today: Date,
  sowMonth?: number,
  sowWeekOfMonth?: number
): number | null {
  if (sowMonth === undefined || sowWeekOfMonth === undefined) return null;

  const todayDay = startOfLocalDay(today);
  const weekStart = dateForSowWeekOfMonth(today.getFullYear(), sowMonth, sowWeekOfMonth);
  const weekEnd = endOfSowWeek(weekStart);

  if (todayDay.getTime() > weekEnd.getTime()) {
    return Math.round((weekStart.getTime() - todayDay.getTime()) / MS_PER_DAY);
  }
  if (todayDay.getTime() < weekStart.getTime()) {
    return Math.round((weekStart.getTime() - todayDay.getTime()) / MS_PER_DAY);
  }
  return 0;
}

function calculatePlantCounts(gardenPlants: Garden['plants']): Record<string, number> {
  const counts: Record<string, number> = {};

  gardenPlants.forEach((plant) => {
    counts[plant.plantId] = (counts[plant.plantId] || 0) + 1;
  });

  return counts;
}

export function generateSeedRequirements(
  garden: Garden | null,
  plants: Plant[],
  sownStatus: Record<string, boolean> = {},
  configs: PlantSowingConfig[] = [],
  plantDefaults: UserPlantDefault[] = [],
  preferences: UserPreferences | null | undefined = undefined,
  today: Date = new Date()
): SeedRequirement[] {
  if (!garden) return [];

  const plantCounts = calculatePlantCounts(garden.plants);
  const requirements: SeedRequirement[] = [];
  const usedPlantIds = new Set(garden.plants.map((p) => p.plantId));

  plants.forEach((plant) => {
    if (!usedPlantIds.has(plant.id)) return;

    const config = getSowingConfigForPlant(
      configs,
      garden._id,
      plant.id,
      plant.category,
      plantDefaults,
      plant,
      preferences
    );
    if (!config.isActive) return;

    requirements.push({
      plantId: plant.id,
      plantName: plant.name,
      emoji: plant.emoji,
      imagePath: plant.imagePath,
      category: plant.category,
      seedsNeeded: plantCounts[plant.id] || 0,
      hasSown: sownStatus[plant.id] ?? false,
      sowTimingLabel: formatSowTimingLabel(config.sowMonth, config.sowWeekOfMonth),
      daysUntilSow: daysUntilSowTiming(today, config.sowMonth, config.sowWeekOfMonth),
    });
  });

  return requirements.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return a.plantName.localeCompare(b.plantName);
  });
}

type ToggleSeedSownFn = (args: {
  gardenId: Id<'gardens'>;
  plantId: string;
  hasSown: boolean;
}) => Promise<unknown>;

export function createHandleToggleSown(
  selectedGarden: Garden | null,
  toggleSeedSown: ToggleSeedSownFn
) {
  return async (plantId: string, currentStatus: boolean) => {
    if (!selectedGarden) return;
    await toggleSeedSown({
      gardenId: selectedGarden._id,
      plantId,
      hasSown: !currentStatus,
    });
  };
}
